/**
 * Which of the two tables a county can actually fill.
 *
 * The counties do not all publish the same shape of record, and two of the
 * three publish only one of them: Palm Beach's coverage is a building
 * register with no parcels behind it, and a county whose ingest produces no
 * condo rollup has parcels and no buildings. The dashboard opens on Buildings,
 * so a county holding none of them opened on an empty table with its rows one
 * unmarked tab away — and the empty state guessed at a cause ("no source is
 * wired for this county yet") that was false in both directions.
 *
 * The counts come from `/api/counties`, which is unfiltered, so a zero there
 * means the county genuinely holds no row of that shape rather than that the
 * current filters matched nothing. That distinction is the whole point: this
 * module must never be fed a filtered count, or it will disable a tab because
 * someone dragged a slider.
 *
 * Pure functions with tests rather than conditionals inside `App`, for the
 * same reason `route.ts` is: what the product shows when it has nothing to
 * show is a product decision, not an accident of `if` placement.
 */

import type { County } from "../api/types";
import type { View } from "./filters";

/** Just the counts, so callers can pass a stub and tests need no fixture. */
export type Held = Pick<County, "parcels" | "buildings"> & { name?: string };

/**
 * Whether the county holds any row of each shape.
 *
 * With no entry — "all covered counties", or the registry not fetched yet —
 * both are held. A tab must never be disabled because a request is in flight.
 */
export function heldViews(entry?: Held): Record<View, boolean> {
  if (!entry) return { buildings: true, units: true };
  return { buildings: entry.buildings > 0, units: entry.parcels > 0 };
}

/**
 * The view to actually show.
 *
 * Falls through to the shape the county has rather than rendering an empty
 * table for the shape it does not. If it holds neither, the request stands as
 * made: there is nothing better to switch to, and silently changing tabs would
 * be a worse answer than an honest empty state.
 */
export function resolveView(requested: View, entry?: Held): View {
  const held = heldViews(entry);
  if (held[requested]) return requested;
  if (held.buildings) return "buildings";
  if (held.units) return "units";
  return requested;
}

/**
 * Why a view is empty for this county, or null if it is not.
 *
 * Deliberately generic. Naming what each county publishes reads better and
 * goes stale the moment a county gains a feed — which is exactly how the
 * masthead came to tell Broward users it had no permit history months after it
 * did. The counts are live, so this sentence cannot outlive its own truth.
 */
export function unavailableReason(view: View, entry?: Held): string | null {
  if (heldViews(entry)[view]) return null;
  const where = entry?.name ? `${entry.name} County` : "This county";
  const noun = view === "buildings" ? "building-level records" : "parcel records";
  return `${where} publishes no ${noun} we can source. See the coverage map for what it does hold.`;
}
